import React from 'react';
import type { IconName } from '@repo/design-system';
import { matchPlanEntities, PLAN_STATE_LABELS } from '@repo/front-end-utils';
import type { PlanSearchEntry, PlanSearchKind } from '@repo/front-end-utils';
import { TitleBarSearch } from './TitleBarSearch';
import type { TitleBarSearchProps, TitleBarSearchTypeStatus } from './TitleBarSearch';

/**
 * The Floor plan and Observers steps' title-bar search (`onboarding-polish-search`, spec FR3): the
 * shared `TitleBarSearch` shell bound to plan entities, matched by the one shared matcher,
 * `matchPlanEntities`. The keyboard model, the highlight held by key, the announcements and the
 * equipment-type note are the shell's (moved there by `composites-page-search`, spec FR1, so the
 * Composite measures step binds the same behaviour); what is here is what a plan entity looks like
 * in the list.
 *
 * On the Floor plan step it also lists the register's rows that are not on the plan
 * (`floor-plan-page-search`, R3): kind `register`, read as *Not on plan* or *Keep off plan* in the
 * lavender trailing word, with the customer's IDs in their line, and unavailable to pick — Enter
 * explains them instead. The Floor plan also passes its own wording, the active query it lights,
 * Enter's frame and the register's note; the other steps pass none and are unchanged.
 *
 * The same component serves the Floor plan step (mounted by `OnboardingPage`) and the Observers step
 * (portalled into the title bar by `OnboardingObservers`, which owns the camera feeds it searches).
 * What a pick DOES is the caller's: this only says which entry was picked.
 */

export type PlanSearchTypeStatus = TitleBarSearchTypeStatus;

export interface PlanEntitySearchProps {
  entries: ReadonlyArray<PlanSearchEntry>;
  /** The twin's BID, so a bare code tail finds its full code. */
  twinBid?: string | null;
  onPick: (entry: PlanSearchEntry) => void;
  /**
   * Loads the equipment types: on the box's first focus, from the list's Retry, and on any focus
   * while `typeStatus` is `error` — the keyboard's way to retry, because the Retry button inside the
   * list cannot take focus (focus stays in the box).
   */
  onLoadTypes?: () => void;
  /** Where the equipment-type catalogue stands; `ready` and `idle` show nothing. */
  typeStatus?: PlanSearchTypeStatus;
  /** The key that focuses the box (`TitleBarSearch`'s `shortcut`); only the Floor plan step passes it. */
  shortcut?: string;
  /** The box's width (`TitleBarSearch`'s `width`). */
  width?: string;
  /** The results list's width (`TitleBarSearch`'s `listWidth`). */
  listWidth?: string;
  /** The box's accessible name. Default *Search the plan*; the Floor plan step names the register too. */
  label?: string;
  /** Default *Search the plan…*; the Floor plan step passes the design's (`floor-plan-page-search`, R8). */
  placeholder?: string;
  /** The list's sentence when nothing matches. Default *Nothing on this twin matches "…".* */
  emptyText?: (query: string) => string;
  /** `TitleBarSearch`'s, passed through (R4.1). */
  onActiveQueryChange?: TitleBarSearchProps<PlanSearchEntry>['onActiveQueryChange'];
  /** `TitleBarSearch`'s, passed through (R5.1). */
  onSubmit?: TitleBarSearchProps<PlanSearchEntry>['onSubmit'];
  /** `TitleBarSearch`'s, passed through (R9). */
  sourceNote?: TitleBarSearchProps<PlanSearchEntry>['sourceNote'];
}

const LABEL = 'Search the plan';
const PLACEHOLDER = 'Search the plan…';

const KIND_ICON: Record<PlanSearchKind, IconName> = {
  surface: 'draw-square',
  item: 'dumbbell',
  camera: 'video',
  feed: 'video',
  // Never on the Floor plan or Observers steps — only the Composite measures step adds them.
  floor: 'layer-group',
  twin: 'building',
  // A register row not on the plan — the Floor plan step only (`floor-plan-page-search`, R3).
  register: 'dumbbell',
};

/** The kind as a word, because an icon alone is not a state (Block A, criterion 16). */
const KIND_WORD: Record<PlanSearchKind, string> = {
  surface: 'area',
  item: 'machine',
  camera: 'camera',
  feed: 'camera',
  floor: 'floor',
  twin: 'whole twin',
  register: 'register row',
};

const keyOf = (e: PlanSearchEntry) => `${e.kind}:${e.refid}`;

/** A register row's state, in the Equipment page's words (`PLAN_STATE_LABELS`). */
const planStateLabel = (e: PlanSearchEntry): string | null =>
  e.planState ? PLAN_STATE_LABELS[e.planState] : null;

/** Everything the row says, in words — the option's accessible name (R3.2). */
const optionName = (e: PlanSearchEntry): string =>
  [
    e.label,
    KIND_WORD[e.kind],
    // The outline glyph's meaning in words, as the plan's marker says it (review).
    e.kind === 'camera' && e.linked === false ? 'no stream' : null,
    planStateLabel(e),
    e.code || null,
    e.customerId ? `customer ID ${e.customerId}` : null,
    // A register row's line also shows its source ID and its type or product (review).
    e.sourceId ? `source ID ${e.sourceId}` : null,
    e.kind === 'register' ? e.detail || null : null,
    e.floorName,
  ]
    .filter(Boolean)
    .join(', ');

/**
 * The line under the label. A plan entity reads code · type · customer ID (R1.3); a register row
 * reads customer ID · source ID · type or product, as frame 08's register list does (R3.1).
 */
const optionMeta = (e: PlanSearchEntry): string =>
  (e.kind === 'register' ? [e.customerId, e.sourceId, e.detail] : [e.code, e.detail, e.customerId])
    .filter(Boolean)
    .join(' · ') || KIND_WORD[e.kind];

/** A register row explains a miss; it is not something to select (R3.3, Rob 2026-10-13). */
const isPickable = (e: PlanSearchEntry) => e.kind !== 'register';

const unpickableText = (e: PlanSearchEntry): string =>
  e.planState === 'keep_off_plan'
    ? `${e.label} is in the register and marked Keep off plan.`
    : `${e.label} is in the register but not on the plan.`;

const defaultEmptyText = (query: string) => `Nothing on this twin matches “${query}”.`;

export function PlanEntitySearch({
  entries,
  twinBid,
  onPick,
  onLoadTypes,
  typeStatus = 'idle',
  shortcut,
  width,
  listWidth,
  label = LABEL,
  placeholder = PLACEHOLDER,
  emptyText = defaultEmptyText,
  onActiveQueryChange,
  onSubmit,
  sourceNote,
}: PlanEntitySearchProps) {
  const match = React.useCallback(
    (query: string) => matchPlanEntities(entries, query, { twinBid }),
    [entries, twinBid],
  );
  return (
    <TitleBarSearch<PlanSearchEntry>
      label={label}
      placeholder={placeholder}
      match={match}
      keyOf={keyOf}
      option={entry => ({
        icon: KIND_ICON[entry.kind],
        // A linked camera's glyph is solid, an unlinked one outline (as on the plan's markers).
        iconVariant: entry.kind === 'camera' && entry.linked ? 'solid' : undefined,
        label: entry.label,
        meta: optionMeta(entry),
        trailing: planStateLabel(entry) ?? entry.floorName ?? undefined,
        trailingTone: entry.planState ? 'lav' : undefined,
        name: optionName(entry),
      })}
      emptyText={emptyText}
      isPickable={isPickable}
      unpickableText={unpickableText}
      onActiveQueryChange={onActiveQueryChange}
      onSubmit={onSubmit}
      sourceNote={sourceNote}
      pickedText={entry =>
        entry.floorName
          ? `Selected ${entry.label} on ${entry.floorName}.`
          : `Selected ${entry.label}.`
      }
      onPick={onPick}
      onLoadTypes={onLoadTypes}
      typeStatus={typeStatus}
      shortcut={shortcut}
      width={width}
      listWidth={listWidth}
    />
  );
}
